/**
 * @fileoverview Shared pass/fail state computation for fail-capable widgets.
 * @module core-domain/inspection
 *
 * Responsibilities:
 * - Single source of truth for "is this widget in a fail state?" and "is this
 *   widget pass/fail capable?" so every surface (the FieldWrapper status pill,
 *   the ToggleField track color, the FieldViolationCaptures failure surface,
 *   AND the canonical completion validator) agrees.
 * - Translate the Properties Panel inputs (`extras.hasPassFail`, `extras.failsOn`,
 *   `extras.failValues`) into a uniform model for every fail-capable widget
 *   (toggle, dropdown, radioGroup, checkboxGroup).
 *
 * Dependencies:
 * - `./types` `InspectionFormField`.
 *
 * Architecture:
 * - Pure functions. No React, no host coupling. Lives in core-domain (below the
 *   renderer) so both web apps and the server-side completion mirror can share
 *   the exact same fail-state rule.
 *
 * Notes:
 * - Violation code bindings (`field.violationMappings`) are metadata only here.
 *   The pass/fail state is owned solely by `extras.hasPassFail` plus
 *   `failsOn`/`failValues`, NOT by whether a violation code is selected. This
 *   matches the inspection-template design: the "this field has a pass/fail
 *   state" checkbox is the sole driver of pass/fail UX.
 */
import type { InspectionFormField } from './types';

/**
 * @contract PassFailExtras
 * Purpose: the pass/fail-related slice of a field's `extras` bag.
 * Fields: hasPassFail, failsOn, failValues, surface-toggle + require-on-fail flags.
 * Rules: only meaningful on fail-capable widget types.
 * Sync: read-only projection of template config; never persisted separately.
 */
export type PassFailExtras = {
  hasPassFail?: boolean;
  /** Toggle: which boolean side is considered failing. */
  failsOn?: boolean;
  /** Dropdown / radio / checkboxGroup: option values that are considered failing. */
  failValues?: string[];
  /** Surface-toggle flags (interpreted by FieldViolationCaptures). */
  showNotesAlways?: boolean;
  showCameraAlways?: boolean;
  requireNotesOnFail?: boolean;
  requirePhotosOnFail?: boolean;
  requireLocationOnFail?: boolean;
};

/**
 * Field types that may carry pass/fail semantics. Other widget types ignore
 * `extras.hasPassFail` entirely.
 */
const FAIL_CAPABLE_TYPES = new Set<string>(['toggle', 'dropdown', 'radioGroup', 'checkboxGroup']);

/** Read the pass/fail slice of a field's `extras` bag. */
export function getPassFailExtras(field: InspectionFormField): PassFailExtras {
  return (field.extras ?? {}) as PassFailExtras;
}

/** True iff the widget type can carry pass/fail semantics. */
export function isPassFailCapableType(field: InspectionFormField): boolean {
  return FAIL_CAPABLE_TYPES.has(field.type);
}

/**
 * @process resolveToggleFailsOn
 * Steps: Resolve which boolean side of a toggle is the FAILING side.
 *   1. `extras.failsOn` when explicitly set (the Properties Panel writes this).
 *   2. The field's violation-mapping trigger (`equals <boolean>`) — authoritative
 *      fail side persisted on every mapped toggle, present even on snapshots /
 *      legacy fields that predate `failsOn`.
 *   3. Fall back to `false` (OFF means fail) only when neither source exists.
 * Inputs: field. Outputs: the boolean value that represents failure.
 * Idempotency: REQUIRED (pure).
 */
export function resolveToggleFailsOn(field: InspectionFormField): boolean {
  const extras = getPassFailExtras(field);
  if (typeof extras.failsOn === 'boolean') return extras.failsOn;

  const mappings = (field as { violationMappings?: Array<{ bindingMode?: string; trigger?: { op?: string; value?: unknown } }> }).violationMappings;
  if (Array.isArray(mappings)) {
    for (const mapping of mappings) {
      // G-024 — pending-tenant-bind mappings are unresolved and must not
      // influence the fail-side resolution; skip them so we fall back to
      // the default (false = OFF means fail) when no bound mapping exists.
      if (mapping?.bindingMode === 'pending-tenant-bind') continue;
      const trigger = mapping?.trigger;
      if (trigger && trigger.op === 'equals' && typeof trigger.value === 'boolean') {
        return trigger.value;
      }
    }
  }
  return false;
}

/**
 * True iff the field is configured as a pass/fail state widget. This is the
 * single switch that drives the colored status pill, the widget's pass/fail
 * coloring, and the failure capture surface.
 */
export function isPassFailField(field: InspectionFormField): boolean {
  if (!isPassFailCapableType(field)) return false;
  return !!getPassFailExtras(field).hasPassFail;
}

/**
 * @process isPassFailFailing
 * Steps: True iff the field is currently in a failing state per its hasPassFail
 *   configuration. Returns false when the field has no value yet, when
 *   hasPassFail is not set, or when `isNA` is true (N/A suppresses the fail
 *   verdict so that no violation is generated for an N/A'd field).
 * Inputs: field, current value, optional isNA flag. Outputs: boolean.
 * Idempotency: REQUIRED (pure).
 */
export function isPassFailFailing(field: InspectionFormField, value: unknown, isNA?: boolean): boolean {
  if (isNA) return false;
  if (!isPassFailField(field)) return false;
  const extras = getPassFailExtras(field);
  switch (field.type) {
    case 'toggle': {
      if (typeof value !== 'boolean') return false;
      const failsOn = resolveToggleFailsOn(field);
      return value === failsOn;
    }
    case 'dropdown':
    case 'radioGroup': {
      if (typeof value !== 'string' || value.length === 0) return false;
      return Array.isArray(extras.failValues) && extras.failValues.includes(value);
    }
    case 'checkboxGroup': {
      if (!Array.isArray(value) || value.length === 0) return false;
      if (!Array.isArray(extras.failValues) || extras.failValues.length === 0) return false;
      return (value as string[]).some((v) => extras.failValues!.includes(v));
    }
    default:
      return false;
  }
}

/**
 * True iff the field has been "answered" enough that the pass/fail pill can
 * commit to a pass or fail verdict (vs. staying neutral).
 */
export function hasPassFailVerdict(field: InspectionFormField, value: unknown): boolean {
  if (!isPassFailField(field)) return false;
  switch (field.type) {
    case 'toggle':
      return typeof value === 'boolean';
    case 'dropdown':
    case 'radioGroup':
      return typeof value === 'string' && value.length > 0;
    case 'checkboxGroup':
      return Array.isArray(value) && value.length > 0;
    default:
      return false;
  }
}

export type PassFailStatus = 'pass' | 'fail' | 'neutral';

/**
 * @process getPassFailStatus
 * Steps: Compute the FieldWrapper pass/fail pill state from extras + value.
 *   Returns `undefined` for fields that are NOT pass/fail capable (so the pill
 *   is not rendered at all). `neutral` until a verdict can be committed.
 * Inputs: field, value, isNA. Outputs: PassFailStatus | undefined.
 * Idempotency: REQUIRED (pure).
 */
export function getPassFailStatus(
  field: InspectionFormField,
  value: unknown,
  isNA?: boolean,
): PassFailStatus | undefined {
  if (isNA) return undefined;
  if (!isPassFailField(field)) return undefined;
  if (!hasPassFailVerdict(field, value)) return 'neutral';
  return isPassFailFailing(field, value) ? 'fail' : 'pass';
}
